import React, { useState } from "react";
import {
  FaCheck,
  FaTimes,
  FaThumbsUp,
  FaThumbsDown,
  FaQuestionCircle,
  FaMoneyBillWave,
  FaHandshake,
  FaStickyNote,
} from "react-icons/fa";
import "./AppointmentCompletionModal.css";

const AppointmentCompletionModal = ({
  appointment,
  isOpen,
  onClose,
  onComplete,
  isLoading,
  showToast,
}) => {
  const [completionData, setCompletionData] = useState({
    status: "", // "successful", "unsuccessful", "considering"
    proposedPrice: "",
    agreedPrice: "",
    notes: "",
  });

  const handleStatusChange = (status) => {
    setCompletionData((prev) => ({ ...prev, status }));
  };

  const handleInputChange = (field, value) => {
    setCompletionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!completionData.status) {
      showToast && showToast("Lütfen randevu sonucunu seçin.", "warning");
      return;
    }

    // Validate required price fields
    if (!completionData.proposedPrice || !completionData.agreedPrice) {
      showToast &&
        showToast(
          "Lütfen hem teklif edilen hem de anlaşılan fiyatı girin.",
          "warning"
        );
      return;
    }

    // Validate price values are positive numbers
    const proposedPrice = parseFloat(completionData.proposedPrice);
    const agreedPrice = parseFloat(completionData.agreedPrice);

    if (isNaN(proposedPrice) || proposedPrice <= 0) {
      showToast &&
        showToast("Lütfen geçerli bir teklif edilen fiyat girin.", "warning");
      return;
    }

    if (isNaN(agreedPrice) || agreedPrice <= 0) {
      showToast &&
        showToast("Lütfen geçerli bir anlaşılan fiyat girin.", "warning");
      return;
    }

    onComplete(appointment.id, completionData);
    resetForm();
  };

  const resetForm = () => {
    setCompletionData({
      status: "",
      proposedPrice: "",
      agreedPrice: "",
      notes: "",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="completion-modal-overlay" onClick={handleClose}>
      <div className="completion-modal" onClick={(e) => e.stopPropagation()}>
        <div className="completion-modal-header">
          <h3>Randevu Tamamlama</h3>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={isLoading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="completion-modal-content">
          {/* Appointment Info */}
          <div className="appointment-info">
            <h4>Randevu Bilgileri</h4>
            <div className="info-row">
              <span className="info-label">Öğrenci:</span>
              <span className="info-value">
                {appointment.userData?.firstName ||
                  appointment.userInfo?.firstName}{" "}
                {appointment.userData?.lastName ||
                  appointment.userInfo?.lastName}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Tarih:</span>
              <span className="info-value">{appointment.date}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Saat:</span>
              <span className="info-value">{appointment.time}</span>
            </div>
          </div>

          {/* Status Selection */}
          <div className="status-section">
            <h4>Randevu Sonucu</h4>
            <div className="status-options">
              <button
                className={`status-option successful ${
                  completionData.status === "successful" ? "selected" : ""
                }`}
                onClick={() => handleStatusChange("successful")}
                disabled={isLoading}
              >
                <FaThumbsUp className="status-icon" />
                <span>Başarılı</span>
              </button>
              <button
                className={`status-option unsuccessful ${
                  completionData.status === "unsuccessful" ? "selected" : ""
                }`}
                onClick={() => handleStatusChange("unsuccessful")}
                disabled={isLoading}
              >
                <FaThumbsDown className="status-icon" />
                <span>Başarısız</span>
              </button>
              <button
                className={`status-option considering ${
                  completionData.status === "considering" ? "selected" : ""
                }`}
                onClick={() => handleStatusChange("considering")}
                disabled={isLoading}
              >
                <FaQuestionCircle className="status-icon" />
                <span>Düşünüyor</span>
              </button>
            </div>
          </div>

          {/* Price Information */}
          <div className="price-section">
            <h4>Fiyat Bilgileri</h4>
            <div className="price-inputs">
              <div className="input-group">
                <label htmlFor="proposedPrice">
                  <FaMoneyBillWave className="input-icon" />
                  Teklif Edilen Fiyat (€) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="proposedPrice"
                  placeholder="Örn: 5000"
                  value={completionData.proposedPrice}
                  onChange={(e) =>
                    handleInputChange("proposedPrice", e.target.value)
                  }
                  disabled={isLoading}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label htmlFor="agreedPrice">
                  <FaHandshake className="input-icon" />
                  Anlaşılan Fiyat (€) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="agreedPrice"
                  placeholder="Örn: 4500"
                  value={completionData.agreedPrice}
                  onChange={(e) =>
                    handleInputChange("agreedPrice", e.target.value)
                  }
                  disabled={isLoading}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="notes-section">
            <h4>Notlar</h4>
            <div className="input-group">
              <label htmlFor="notes">
                <FaStickyNote className="input-icon" />
                Görüşme Notları (İsteğe bağlı)
              </label>
              <textarea
                id="notes"
                placeholder="Görüşme ile ilgili önemli notları buraya yazabilirsiniz..."
                value={completionData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                rows={4}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <div className="completion-modal-actions">
          <button
            className="cancel-button"
            onClick={handleClose}
            disabled={isLoading}
          >
            İptal
          </button>
          <button
            className="complete-button"
            onClick={handleSubmit}
            disabled={isLoading || !completionData.status}
          >
            <FaCheck className="button-icon" />
            {isLoading ? "Kaydediliyor..." : "Tamamla"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentCompletionModal;
